import { useEffect, useMemo, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { createBrandMuiTheme } from "./brandMuiTheme";

function readMode() {
  const selected = document.documentElement.getAttribute("data-theme");
  return selected === "dark" ||
    (selected === "auto" &&
      window.matchMedia?.("(prefers-color-scheme: dark)").matches)
    ? "dark"
    : "light";
}

function resolvedColors() {
  const styles = getComputedStyle(document.documentElement);
  const tokens = {
    accent: "accent",
    accentHover: "accent-hover",
    onAccent: "on-accent",
    background: "bg",
    surface: "card",
    text: "text",
    muted: "text-muted",
    border: "border",
    success: "success",
    danger: "danger",
    warning: "warning",
    gold: "gold",
    onGold: "on-gold",
  };
  return Object.fromEntries(
    Object.entries(tokens)
      .map(([name, token]) => [
        name,
        styles.getPropertyValue(`--color-${token}`).trim(),
      ])
      .filter(([, value]) => value),
  );
}

// The existing theme switch controls data-theme. Observe it instead of adding
// a second preference writer, so portals and legacy MUI forms follow the shell.
export default function AppThemeProvider({ children }) {
  const [mode, setMode] = useState(readMode);
  useEffect(() => {
    const update = () => setMode(readMode());
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    const media = window.matchMedia?.("(prefers-color-scheme: dark)");
    media?.addEventListener?.("change", update);
    update();
    return () => {
      observer.disconnect();
      media?.removeEventListener?.("change", update);
    };
  }, []);
  const theme = useMemo(
    () => createBrandMuiTheme(mode, resolvedColors()),
    [mode],
  );
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
